// src/finance/engine/engine.ts
// Cascade de simulation (spec 2026-08-20 § 1.1). Le moteur travaille en mensuel,
// annualise (×12) pour les barèmes IR/CT, puis ramène au mois (/12).
import type { CorporateRegime, FinanceSettings, FinanceTaxSettings, RegimeResult, SimulationResult } from './types'
import { corporationTaxFrAnnual, corporationTaxUkAnnual, irAnnual, round2, salaryAllowanceAnnual } from './tax'

export function simulateRegime(
  revenueMonthly: number,
  share: number,
  settings: FinanceSettings,
  companyExpensesMonthly: number,
  corporateRegime: CorporateRegime = 'uk',
): RegimeResult {
  const { tax } = settings
  const rawEnvelope = revenueMonthly - companyExpensesMonthly
  const isLoss = rawEnvelope < 0
  const envelope = Math.max(0, rawEnvelope)

  // Canal salaire : part de l'enveloppe = coût employeur total (brut + patronales).
  const employerCost = envelope * share
  const gross = employerCost / (1 + tax.employerRate)
  const employerContrib = gross * tax.employerRate
  const employeeContrib = gross * tax.employeeRate
  const netBeforeIr = gross - employeeContrib
  // Base imposable = net à payer + CSG non déductible + CRDS (fraction du brut) :
  // le droit français impose le net IMPOSABLE, pas le net après TOUTES cotisations.
  // `netBeforeIr` reste le net à payer — seule la base de l'abattement/barème change.
  const taxableBase = netBeforeIr + gross * tax.nonDeductibleRate
  const allowance = salaryAllowanceAnnual(taxableBase * 12, tax)
  const taxableAnnual = Math.max(0, taxableBase * 12 - allowance)
  const ir = irAnnual(taxableAnnual, tax) / 12
  const salaryNet = netBeforeIr - ir

  // Canal dividendes : le reste de l'enveloppe est un bénéfice avant impôt société
  // (CT UK ou IS français selon la juridiction simulée — spec comparatif 2026-08-20).
  const profitBeforeCt = envelope * (1 - share)
  const ctAnnualFn = corporateRegime === 'fr' ? corporationTaxFrAnnual : corporationTaxUkAnnual
  const ct = ctAnnualFn(profitBeforeCt * 12, tax) / 12
  const distributable = profitBeforeCt - ct
  const pfu = distributable * tax.pfuRate
  const dividendsNet = distributable - pfu

  const netMonthly = salaryNet + dividendsNet
  return {
    share,
    revenueMonthly: round2(revenueMonthly),
    revenueAnnual: round2(revenueMonthly * 12),
    tjm: settings.billableDays > 0 ? round2(revenueMonthly / settings.billableDays) : null,
    netMonthly: round2(netMonthly),
    netAnnual: round2(netMonthly * 12),
    isLoss,
    breakdown: {
      companyExpenses: round2(companyExpensesMonthly),
      envelope: round2(envelope),
      salary: {
        employerCost: round2(employerCost),
        gross: round2(gross),
        employerContrib: round2(employerContrib),
        employeeContrib: round2(employeeContrib),
        netBeforeIr: round2(netBeforeIr),
        taxable: round2(taxableAnnual / 12),
        ir: round2(ir),
        net: round2(salaryNet),
      },
      dividends: {
        profitBeforeCt: round2(profitBeforeCt),
        ct: round2(ct),
        distributable: round2(distributable),
        pfu: round2(pfu),
        net: round2(dividendsNet),
      },
    },
  }
}

export function simulate(
  revenueMonthly: number,
  settings: FinanceSettings,
  companyExpensesMonthly: number,
  corporateRegime: CorporateRegime = 'uk',
): SimulationResult {
  return {
    fullSalary: simulateRegime(revenueMonthly, 1, settings, companyExpensesMonthly, corporateRegime),
    fullDividends: simulateRegime(revenueMonthly, 0, settings, companyExpensesMonthly, corporateRegime),
    mix: simulateRegime(revenueMonthly, settings.mixSalaryShare, settings, companyExpensesMonthly, corporateRegime),
  }
}

/** Entrées de `estimateNetForMonth` — tous les montants sont des EUR versés (nets à payer). */
export type MonthActuals = {
  /** Somme des salaires nets versés depuis janvier, MOIS COURANT EXCLU. */
  salaryYearToDateBefore: number
  /** Salaire net versé ce mois-ci. */
  salaryThisMonth: number
  /** Dividendes versés ce mois-ci. */
  dividendsThisMonth: number
}

/**
 * Net à payer → net IMPOSABLE, la base que `simulateRegime` impose depuis C1.
 *
 * Le suivi ne transporte que des nets VERSÉS : le brut se reconstitue par
 * `net / (1 − employeeRate)`, d'où
 *   base = net + brut × nonDeductibleRate = net × (1 + nonDeductibleRate / (1 − employeeRate)).
 * Avec les défauts, le coefficient vaut 1,036538 — appliqué à 4 756,51 €/mois il
 * redonne exactement les 4 930,31 € de base du Calculateur.
 *
 * Sans cette conversion, le Suivi imposait le net à payer et le Calculateur le net
 * imposable : pour douze mois pile sur l'objectif, le Suivi annonçait 563,03 €/an
 * de net en trop et affichait « objectif dépassé » sur une année simplement conforme.
 *
 * Garde-fou : à `employeeRate ≥ 1` le brut n'est pas reconstituable (division par
 * zéro ou négative). On retombe alors sur le net lui-même — jamais sur ±∞ ni NaN.
 */
export function taxableFromNetPaid(netPaid: number, tax: FinanceTaxSettings): number {
  const employeeShare = 1 - tax.employeeRate
  if (employeeShare <= 0) return netPaid
  return netPaid * (1 + tax.nonDeductibleRate / employeeShare)
}

/**
 * Net perso d'un mois réel (suivi § 3), calculé en DIFFÉRENTIEL sur le cumul annuel.
 *
 * Contrat pour les appelants (module de suivi) :
 * - passer le CUMUL des versements de janvier au mois précédent (`*YearToDateBefore`)
 *   et les montants du mois courant ; les cumuls n'incluent JAMAIS le mois courant ;
 * - la valeur rendue est le net du MOIS : `salaire du mois − (IR(cumul avec ce mois)
 *   − IR(cumul avant ce mois)) + dividendes du mois après PFU` ;
 * - sommer les douze appels d'une année redonne exactement le net annuel : l'IR
 *   télescope, aucun mois n'est extrapolé ×12.
 *
 * L'IR est progressif, donc seul le cumul a un sens ; le PFU est un taux plat,
 * il reste calculé directement sur le mois — c'est pourquoi aucun cumul de
 * dividendes n'est demandé.
 *
 * L'assiette est le net IMPOSABLE (`taxableFromNetPaid`), la même que celle du
 * Calculateur : sans cela les deux vues rendaient deux IR différents pour le
 * même salaire.
 *
 * Remplace `estimateNetFromActuals`, qui annualisait un mois isolé (×12) et
 * surestimait l'IR dès que les versements étaient irréguliers (erreur mesurée :
 * +5 923 € sur une année à un seul gros versement). Cette dernière est conservée
 * le temps que le module de suivi migre.
 */
export function estimateNetForMonth(actuals: MonthActuals, settings: FinanceSettings): number {
  const { tax } = settings
  const before = Math.max(0, actuals.salaryYearToDateBefore)
  const after = before + Math.max(0, actuals.salaryThisMonth)
  const irOn = (salaryCumulated: number): number => {
    // MÊME assiette que simulateRegime (C1) : le net imposable, pas le net à payer.
    const base = taxableFromNetPaid(salaryCumulated, tax)
    const allowance = salaryAllowanceAnnual(base, tax)
    return irAnnual(Math.max(0, base - allowance), tax)
  }
  const irThisMonth = Math.max(0, irOn(after) - irOn(before))
  const dividendsNet = Math.max(0, actuals.dividendsThisMonth) * (1 - tax.pfuRate)
  return round2(Math.max(0, Math.max(0, actuals.salaryThisMonth) - irThisMonth) + dividendsNet)
}

/**
 * @deprecated Utiliser `estimateNetForMonth`, qui raisonne sur le cumul annuel.
 * Net perso estimé d'un mois réel : salaire net saisi − IR estimé
 * (annualisation ×12, abattement 10 %) + dividendes saisis après PFU.
 * Conservée pour ne pas casser le module de suivi tant qu'il n'a pas migré.
 */
export function estimateNetFromActuals(
  salaryPaidMonthly: number,
  dividendsPaidMonthly: number,
  settings: FinanceSettings,
): number {
  const { tax } = settings
  const allowance = salaryAllowanceAnnual(salaryPaidMonthly * 12, tax)
  const taxableAnnual = Math.max(0, salaryPaidMonthly * 12 - allowance)
  const ir = irAnnual(taxableAnnual, tax) / 12
  const dividendsNet = Math.max(0, dividendsPaidMonthly) * (1 - tax.pfuRate)
  return round2(Math.max(0, salaryPaidMonthly - ir) + dividendsNet)
}
